import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, isNotEmptyArray, isNotNil, Nillable } from '@web/utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AdminBoxDetails } from '../../models/admin-box-details';
import { AdminBoxDataSourceService } from '../../services/admin-box-data-source.service';
import {
  BooleanPipe,
  CurrencyComponent,
  DateComponent,
  DetailsViewComponent,
  ExternalLinkComponent,
  NoDataComponent,
  PrimengDividerDirective,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import {
  AdminMultimediaDataSourceService,
  MultimediaGalleriaThumbnailsComponent,
} from '@web/shared/module/multimedia';
import { Divider } from 'primeng/divider';
import { AdminBoxStatusComponent } from '../../components/admin-box-status/admin-box-status.component';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import { SummernoteHtmlPreviewComponent } from '@web/shared/external';
import { AdminBoxAttributeDetails } from '../../models/admin-box-attribute-details';
import { AdminBoxCharacteristicDetails } from '../../models/admin-box-characteristic-details';
import { AdminBoxLinkComponent } from '../../components/admin-box-link/admin-box-link.component';
import { AdminBoxCosmeticsGridComponent } from '../../components/admin-box-cosmetics-grid/admin-box-cosmetics-grid.component';
import { AdminBoxQuantityHistoryListComponent } from '../admin-box-quantity-history-list/admin-box-quantity-history-list.component';

@Component({
  selector: 'app-admin-box-details',
  imports: [
    CommonModule,
    SpinnerFilledComponent,
    DetailsViewComponent,
    NoDataComponent,
    DateComponent,
    Divider,
    MultimediaGalleriaThumbnailsComponent,
    AdminBoxStatusComponent,
    CurrencyComponent,
    ExternalLinkComponent,
    BooleanPipe,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    SummernoteHtmlPreviewComponent,
    AdminBoxLinkComponent,
    AdminBoxCosmeticsGridComponent,
    AdminBoxQuantityHistoryListComponent,
    PrimengDividerDirective,
  ],
  templateUrl: './admin-box-details.component.html',
  styleUrl: './admin-box-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxDetailsComponent {
  private readonly productDataSource = inject(AdminBoxDataSourceService);
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);

  public readonly id = input.required<Guid>();

  protected readonly id$ = toObservable(this.id);
  protected readonly details$ = this.detailsSource();
  protected readonly isNotNil = isNotNil;
  protected readonly isNotEmptyArray = isNotEmptyArray;
  /* prettier-ignore */
  protected readonly fetchMultimediaDetailsFn = (id: Guid) => this.multimediaDataSource.fetchDetailsById(id, true);

  public shouldShowCharacteristicsAccordions(
    characteristics: Nillable<AdminBoxCharacteristicDetails[]>,
  ): characteristics is AdminBoxCharacteristicDetails[] {
    return isNotNil(characteristics) && isNotEmptyArray(characteristics);
  }

  public shouldShowAttributesAccordions(
    attributes: Nillable<AdminBoxAttributeDetails[]>,
  ): attributes is AdminBoxAttributeDetails[] {
    return isNotNil(attributes) && isNotEmptyArray(attributes);
  }

  private detailsSource(): Observable<AdminBoxDetails> {
    return this.id$.pipe(
      switchMap((id) => this.productDataSource.fetchDetailsById(id)),
    );
  }
}
